(() => {
  if (window.__csGlobalChromeReady) return;
  window.__csGlobalChromeReady = true;

  const focusableSelector = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Set();

  class OverlayCoordinator extends EventTarget {
    constructor() {
      super();
      this.registry = new Map();
      this.current = null;
      this.scrollRestoreToken = 0;
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('keydown', this.onKeydown);
    }

    register(name, options) {
      this.registry.set(name, { name, ...options });
      return () => this.unregister(name);
    }

    unregister(name) {
      if (this.current?.name === name) this.close(name, { restoreFocus: false, immediate: true });
      this.registry.delete(name);
    }

    open(name, trigger) {
      const next = this.registry.get(name);
      if (!next) return false;
      if (this.current?.name === name) return true;
      const handoffScroll = this.current?.locked ? this.current.styleSnapshot?.htmlScrollBehavior : null;
      if (this.current) this.close(this.current.name, { restoreFocus: false, immediate: true });
      // Snapshot the original scroll preference, not the departing overlay's
      // temporary auto override while its two-frame restoration is pending.
      if (handoffScroll) {
        if (handoffScroll.value) document.documentElement.style.setProperty('scroll-behavior', handoffScroll.value, handoffScroll.priority || '');
        else document.documentElement.style.removeProperty('scroll-behavior');
      }

      const root = typeof next.root === 'function' ? next.root() : next.root;
      if (!(root instanceof HTMLElement)) return false;
      const locked = next.lock !== false;
      this.current = { ...next, root, trigger: trigger instanceof HTMLElement ? trigger : document.activeElement, locked };
      if (locked) this.lockPage(this.current);
      else document.documentElement.dataset.csOverlayOpen = name;
      if (!next.preserveVisibility) {
        root.hidden = false;
        root.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => root.classList.add('is-open'));
      }
      next.onOpen?.(this.current);
      if (next.trapFocus !== false) {
        requestAnimationFrame(() => {
          const focusTarget = typeof next.initialFocus === 'function' ? next.initialFocus() : next.initialFocus;
          (focusTarget instanceof HTMLElement ? focusTarget : root.querySelector(focusableSelector) || root).focus({ preventScroll: true });
        });
      }
      this.emitChange('open');
      return true;
    }

    close(name = this.current?.name, options = {}) {
      if (!this.current || this.current.name !== name) return false;
      const active = this.current;
      this.current = null;
      if (!active.preserveVisibility) {
        active.root.classList.remove('is-open');
        active.root.setAttribute('aria-hidden', 'true');
      }
      active.onClose?.(active);
      if (active.locked) this.unlockPage(active);
      else delete document.documentElement.dataset.csOverlayOpen;
      const finish = () => {
        if (!active.preserveVisibility && !active.root.classList.contains('is-open')) active.root.hidden = true;
      };
      if (options.immediate || reducedMotion.matches) finish();
      else window.setTimeout(finish, 440);
      if (options.restoreFocus !== false && active.trigger instanceof HTMLElement && active.trigger.isConnected) {
        requestAnimationFrame(() => active.trigger.focus({ preventScroll: true }));
      }
      this.emitChange('close');
      return true;
    }

    toggle(name, trigger) {
      return this.current?.name === name ? this.close(name) : this.open(name, trigger);
    }

    onKeydown(event) {
      if (!this.current) return;
      if (event.key === 'Escape') {
        if (this.current.handleEscape === false) return;
        event.preventDefault();
        this.close(this.current.name);
        return;
      }
      if (event.key !== 'Tab' || this.current.trapFocus === false) return;
      const controls = [...this.current.root.querySelectorAll(focusableSelector)].filter((element) => element.getClientRects().length && element.getAttribute('aria-hidden') !== 'true');
      if (!controls.length) return;
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.current.root.querySelector('[data-cs-menu-panel]'))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockPage(active) {
      const activeRoot = active.root;
      const scrollPosition = {
        x: Math.max(0, window.scrollX || window.pageXOffset || 0),
        y: Math.max(0, window.scrollY || window.pageYOffset || 0),
      };
      const outside = [];
      let activeBranch = activeRoot;
      while (activeBranch?.parentElement && activeBranch !== document.body) {
        [...activeBranch.parentElement.children].forEach((element) => {
          if (element !== activeBranch && !outside.includes(element)) outside.push(element);
        });
        activeBranch = activeBranch.parentElement;
      }
      active.inertSnapshot = outside.map((element) => ({
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute('aria-hidden'),
      }));
      active.inertSnapshot.forEach(({ element }) => {
        if ('inert' in element) element.inert = true;
        element.setAttribute('aria-hidden', 'true');
        element.dataset.csInert = 'true';
      });

      const readStyle = (style, property) => ({
        value: style.getPropertyValue(property),
        priority: style.getPropertyPriority(property),
      });
      active.scrollPosition = scrollPosition;
      active.styleSnapshot = {
        body: {
          position: readStyle(document.body.style, 'position'),
          top: readStyle(document.body.style, 'top'),
          left: readStyle(document.body.style, 'left'),
          width: readStyle(document.body.style, 'width'),
          overflowY: readStyle(document.body.style, 'overflow-y'),
        },
        htmlScrollBehavior: readStyle(document.documentElement.style, 'scroll-behavior'),
      };

      this.scrollRestoreToken += 1;
      document.documentElement.dataset.csOverlayOpen = this.current?.name || 'true';
      document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
      document.body.style.setProperty('position', 'fixed');
      document.body.style.setProperty('top', `-${scrollPosition.y}px`);
      document.body.style.setProperty('left', `-${scrollPosition.x}px`);
      document.body.style.setProperty('width', '100%');
      document.body.style.setProperty('overflow-y', 'scroll');
    }

    unlockPage(active) {
      (active.inertSnapshot || []).forEach(({ element, inert, ariaHidden }) => {
        if ('inert' in element) element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
        delete element.dataset.csInert;
      });
      delete document.documentElement.dataset.csOverlayOpen;

      const restoreStyle = (style, property, snapshot) => {
        if (snapshot?.value) style.setProperty(property, snapshot.value, snapshot.priority || '');
        else style.removeProperty(property);
      };
      const snapshot = active.styleSnapshot || {};
      restoreStyle(document.body.style, 'position', snapshot.body?.position);
      restoreStyle(document.body.style, 'top', snapshot.body?.top);
      restoreStyle(document.body.style, 'left', snapshot.body?.left);
      restoreStyle(document.body.style, 'width', snapshot.body?.width);
      restoreStyle(document.body.style, 'overflow-y', snapshot.body?.overflowY);

      const position = active.scrollPosition || { x: 0, y: 0 };
      const token = ++this.scrollRestoreToken;
      const restoreScroll = () => window.scrollTo(position.x, position.y);
      restoreScroll();
      requestAnimationFrame(() => {
        if (token !== this.scrollRestoreToken) return;
        restoreScroll();
        requestAnimationFrame(() => {
          if (token !== this.scrollRestoreToken) return;
          restoreScroll();
          restoreStyle(document.documentElement.style, 'scroll-behavior', snapshot.htmlScrollBehavior);
        });
      });
    }

    emitChange(action) {
      const detail = { action, name: this.current?.name || null };
      this.dispatchEvent(new CustomEvent('change', { detail }));
      document.dispatchEvent(new CustomEvent('cs:overlay-change', { detail }));
    }
  }

  const coordinator = window.CorsailleOverlayCoordinator instanceof OverlayCoordinator
    ? window.CorsailleOverlayCoordinator
    : new OverlayCoordinator();
  window.CorsailleOverlayCoordinator = coordinator;

  class AnnouncementRotator {
    constructor(root) {
      this.root = root;
      this.items = [...root.querySelectorAll('[data-cs-announcement-item]')];
      this.index = 0;
      this.timer = 0;
      this.abort = new AbortController();
      if (this.items.length < 2 || root.dataset.csStatic === 'true' || reducedMotion.matches) return;
      document.addEventListener('visibilitychange', () => this.sync(), { signal: this.abort.signal });
      reducedMotion.addEventListener?.('change', () => this.sync(), { signal: this.abort.signal });
      this.sync();
    }

    sync() {
      window.clearInterval(this.timer);
      this.timer = 0;
      if (document.hidden || reducedMotion.matches) return;
      this.timer = window.setInterval(() => {
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, 6000);
    }

    destroy() {
      window.clearInterval(this.timer);
      this.abort.abort();
    }
  }

  class HeaderController {
    constructor(header) {
      this.header = header;
      this.group = header.closest('.shopify-section-group-header-group') || header.parentElement;
      this.announcementGroup = document.querySelector('[data-cs-announcement]')?.closest('.shopify-section-group-header-group') || null;
      this.isHome = header.dataset.csHome === 'true';
      this.hero = this.isHome ? document.querySelector('[data-corsaille-hero]') : null;
      this.homeFlow = this.isHome ? document.querySelector('[data-corsaille-home] .cs-home-flow') : null;
      this.heroTone = this.readHeroTone();
      this.lastY = Math.max(0, window.scrollY);
      this.upwardDistance = 0;
      this.compressed = false;
      this.overlayOpen = false;
      this.stateBeforeOverlay = null;
      this.frame = 0;
      this.abort = new AbortController();
      const { signal } = this.abort;
      window.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      window.addEventListener('resize', () => this.schedule(), { passive: true, signal });
      this.heroObserver = null;
      if (this.hero) {
        this.heroObserver = new MutationObserver(() => {
          this.heroTone = this.readHeroTone();
          this.schedule();
        });
        this.heroObserver.observe(this.hero, { attributes: true, attributeFilter: ['data-active-slide', 'data-header-tone'] });
      }
      coordinator.addEventListener('change', (event) => {
        const isOpening = Boolean(event.detail.name);
        if (isOpening && !this.overlayOpen) this.stateBeforeOverlay = this.header.dataset.csHeaderState || 'solid-light';
        this.overlayOpen = isOpening;
        if (!isOpening && this.stateBeforeOverlay) this.applyState(this.stateBeforeOverlay);
        this.schedule();
      }, { signal });
      this.schedule();
    }

    readHeroTone() {
      if (!this.hero) return 'light';
      if (this.hero.dataset.headerTone === 'dark') return 'dark';
      const active = this.hero.querySelector('[data-hero-slide].is-active');
      return active?.dataset.headerTone === 'dark' ? 'dark' : 'light';
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    applyState(state) {
      this.header.dataset.csHeaderState = state;
      this.group.dataset.csChromeState = state;
      if (this.announcementGroup) this.announcementGroup.dataset.csChromeState = state;
      document.documentElement.dataset.csHeaderState = state;
    }

    update() {
      const y = Math.max(0, window.scrollY);
      const delta = y - this.lastY;
      if (delta < -1) this.upwardDistance += Math.abs(delta);
      else if (delta > 1) this.upwardDistance = 0;

      const homeHeroRect = this.hero?.getBoundingClientRect();
      const announcementHeight = this.announcementGroup?.getBoundingClientRect().height || 0;
      const chromeHeight = this.header.getBoundingClientRect().height + announcementHeight;
      const homeFlowTop = this.homeFlow?.getBoundingClientRect().top;
      const heroDocumentEnd = (this.hero?.offsetTop || 0) + (homeHeroRect?.height || 0);
      const beyondHomeHero = Boolean(this.isHome && this.hero && (
        Number.isFinite(homeFlowTop)
          ? homeFlowTop <= chromeHeight + 1
          : y >= Math.max(0, heroDocumentEnd - chromeHeight)
      ));

      if (this.isHome && this.hero) {
        this.compressed = beyondHomeHero;
        this.upwardDistance = 0;
      } else {
        if (y > 80 && delta > 1) this.compressed = true;
        if (this.compressed && this.upwardDistance >= 28) {
          this.compressed = false;
          this.upwardDistance = 0;
        }
      }

      let state = 'solid-light';
      if (this.overlayOpen) state = 'overlay-open';
      else if (this.isHome && this.hero) {
        state = beyondHomeHero ? 'compressed-light' : `top-transparent-${this.heroTone}`;
      } else if (this.compressed) state = 'compressed-light';

      this.applyState(state);
      if (!this.overlayOpen) this.stateBeforeOverlay = state;
      this.lastY = y;
    }

    destroy() {
      this.abort.abort();
      this.heroObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }

  class MaisonMenu {
    constructor(root, trigger) {
      this.root = root;
      this.trigger = trigger;
      this.panel = root.querySelector('[data-cs-menu-panel]');
      this.abort = new AbortController();
      if (!trigger || !this.panel) return;
      const { signal } = this.abort;
      coordinator.register('menu', {
        root,
        initialFocus: () => this.panel,
        onOpen: () => trigger.setAttribute('aria-expanded', 'true'),
        onClose: () => trigger.setAttribute('aria-expanded', 'false'),
      });
      trigger.addEventListener('click', () => coordinator.toggle('menu', trigger), { signal });
      root.querySelectorAll('[data-cs-menu-disclosure]').forEach((button) => button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        root.querySelectorAll('[data-cs-menu-disclosure]').forEach((control) => {
          const open = control === button && !expanded;
          control.setAttribute('aria-expanded', String(open));
          const region = document.getElementById(control.getAttribute('aria-controls'));
          if (region) region.hidden = !open;
        });
      }, { signal }));
      const menuBreakpoint = window.matchMedia('(max-width: 989px)');
      menuBreakpoint.addEventListener('change', (event) => {
        if (!event.matches && coordinator.current?.name === 'menu') coordinator.close('menu', { restoreFocus: false, immediate: true });
      }, { signal });
      root.querySelectorAll('[data-cs-menu-close]').forEach((button) => button.addEventListener('click', () => coordinator.close('menu'), { signal }));
      root.querySelectorAll('[data-cs-search-open]').forEach((button) => button.addEventListener('click', () => coordinator.open('search', this.trigger), { signal }));
      root.querySelectorAll('[data-ccv-saved-open]').forEach((button) => button.addEventListener('click', () => {
        coordinator.close('menu', { restoreFocus: false, immediate: true });
        this.trigger.focus({ preventScroll: true });
      }, { signal }));
    }

    destroy() {
      coordinator.unregister('menu');
      this.abort.abort();
    }
  }

  class MegaNavigation {
    constructor(root) {
      this.root = root;
      this.toggles = [...root.querySelectorAll('[data-cs-mega-toggle]')];
      this.panels = [...root.querySelectorAll('[data-cs-mega-panel]')];
      this.openName = '';
      this.openTimer = 0;
      this.closeTimer = 0;
      this.abort = new AbortController();
      const { signal } = this.abort;
      const megaBreakpoint = window.matchMedia('(min-width: 990px)');
      megaBreakpoint.addEventListener('change', (event) => { if (!event.matches) this.close(); }, { signal });
      this.toggles.forEach((toggle) => {
        const name = toggle.dataset.csMegaToggle;
        toggle.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          window.clearTimeout(this.closeTimer);
          this.openTimer = window.setTimeout(() => this.open(name), 160);
        }, { signal });
        toggle.addEventListener('pointerleave', () => this.scheduleClose(), { signal });
        toggle.addEventListener('click', (event) => {
          if (event.pointerType === 'touch' && this.openName !== name) {
            event.preventDefault();
            this.open(name, true);
          }
        }, { signal });
        toggle.addEventListener('keydown', (event) => {
          if (['ArrowDown', ' '].includes(event.key)) {
            event.preventDefault();
            this.open(name, true);
          }
        }, { signal });
      });
      this.panels.forEach((panel) => {
        panel.addEventListener('pointerenter', () => window.clearTimeout(this.closeTimer), { signal });
        panel.addEventListener('pointerleave', () => this.scheduleClose(), { signal });
        panel.addEventListener('focusout', (event) => {
          if (!panel.contains(event.relatedTarget) && !this.root.querySelector(`[data-cs-mega-toggle="${CSS.escape(this.openName)}"]`)?.contains(event.relatedTarget)) this.scheduleClose(0);
        }, { signal });
      });
      document.addEventListener('pointerdown', (event) => {
        if (this.openName && !root.contains(event.target)) this.close();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openName) {
          event.preventDefault();
          const trigger = this.root.querySelector(`[data-cs-mega-toggle="${CSS.escape(this.openName)}"]`);
          this.close();
          trigger?.focus();
        }
      }, { signal });
      coordinator.register('mega', {
        root,
        lock: false,
        trapFocus: false,
        preserveVisibility: true,
        onClose: () => this.close(true),
      });
    }

    open(name, focus = false) {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      const trigger = this.root.querySelector(`[data-cs-mega-toggle="${CSS.escape(name)}"]`);
      if (coordinator.current?.name !== 'mega') coordinator.open('mega', trigger);
      this.openName = name;
      this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', String(toggle.dataset.csMegaToggle === name)));
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.csMegaPanel !== name; });
      if (focus) this.root.querySelector(`[data-cs-mega-panel="${CSS.escape(name)}"]`)?.querySelector(focusableSelector)?.focus();
    }

    scheduleClose(delay = 220) {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.close(), delay);
    }

    close(fromCoordinator = false) {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      if (!fromCoordinator && coordinator.current?.name === 'mega') {
        coordinator.close('mega', { restoreFocus: false, immediate: true });
        return;
      }
      this.openName = '';
      this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((panel) => { panel.hidden = true; });
    }

    destroy() {
      this.close();
      coordinator.unregister('mega');
      this.abort.abort();
    }
  }

  class SearchOverlay {
    constructor(root, triggers) {
      this.root = root;
      this.triggers = triggers;
      this.panel = root.querySelector('[data-cs-search-panel]');
      this.input = root.querySelector('[data-cs-search-input]');
      this.reset = root.querySelector('[data-cs-search-reset]');
      this.abort = new AbortController();
      if (!this.panel || !this.input) return;
      const { signal } = this.abort;
      coordinator.register('search', { root, initialFocus: () => this.input });
      triggers.forEach((trigger) => trigger.addEventListener('click', () => coordinator.open('search', trigger), { signal }));
      root.querySelectorAll('[data-cs-search-close]').forEach((button) => button.addEventListener('click', () => coordinator.close('search'), { signal }));
      this.input.addEventListener('input', () => this.syncReset(), { signal });
      this.input.form?.addEventListener('reset', () => requestAnimationFrame(() => { this.syncReset(); this.input.focus(); }), { signal });
      this.syncReset();
    }

    syncReset() {
      if (this.reset) this.reset.hidden = this.input.value.length === 0;
    }

    destroy() {
      coordinator.unregister('search');
      this.abort.abort();
    }
  }

  const registerExternalOverlays = (scope = document) => {
    const cart = document.querySelector('cart-drawer');
    if (cart && !cart.dataset.csOverlayRegistered) {
      const originalOpen = cart.open?.bind(cart);
      const originalClose = cart.close?.bind(cart);
      if (!originalOpen || !originalClose) {
        if (!cart.dataset.csOverlayWaiting) {
          cart.dataset.csOverlayWaiting = 'true';
          customElements.whenDefined('cart-drawer').then(() => {
            delete cart.dataset.csOverlayWaiting;
            registerExternalOverlays(document);
          });
        }
      } else {
        cart.dataset.csOverlayRegistered = 'true';
        coordinator.register('cart', {
          root: cart,
          lock: false,
          trapFocus: false,
          preserveVisibility: true,
          onOpen: (active) => originalOpen(active.trigger),
          onClose: () => originalClose(),
        });
        cart.open = (trigger) => coordinator.open('cart', trigger || cart.activeElement);
        cart.close = () => coordinator.close('cart');
      }
    }

    scope.querySelectorAll('dialog[data-ccv-saved-drawer], dialog[data-cs-gallery-dialog], dialog[data-ccv-modal]').forEach((dialog) => {
      if (dialog.dataset.csOverlayRegistered) return;
      dialog.dataset.csOverlayRegistered = 'true';
      const name = dialog.matches('[data-ccv-saved-drawer]') ? 'saved' : dialog.matches('[data-cs-gallery-dialog]') ? 'media' : 'modal';
      const originalShowModal = dialog.showModal?.bind(dialog);
      const originalClose = dialog.close?.bind(dialog);
      if (!originalShowModal || !originalClose) return;
      coordinator.register(name, {
        root: dialog,
        initialFocus: () => dialog.querySelector('[autofocus], [data-ccv-saved-close], [data-ccv-close], [data-cs-gallery-close]'),
        onOpen: () => { if (!dialog.open) originalShowModal(); },
        onClose: () => { if (dialog.open) originalClose(); },
      });
      dialog.showModal = () => coordinator.open(name, document.activeElement);
      dialog.close = () => {
        if (coordinator.current?.root === dialog) return coordinator.close(name);
        if (dialog.open) return originalClose();
        return undefined;
      };
      dialog.addEventListener('close', () => {
        if (coordinator.current?.root === dialog) coordinator.close(name, { restoreFocus: false, immediate: true });
      });
    });

    scope.querySelectorAll('.mobile-facets__disclosure').forEach((details) => {
      if (details.dataset.csOverlayRegistered) return;
      details.dataset.csOverlayRegistered = 'true';
      coordinator.register('filter', {
        root: details,
        preserveVisibility: true,
        lock: false,
        trapFocus: false,
        handleEscape: false,
        initialFocus: () => details.querySelector('.mobile-facets__close, .mobile-facets__inner'),
        onOpen: () => { if (!details.open) details.open = true; },
        onClose: () => {
          if (!details.open) return;
          const drawer = details.closest('menu-drawer');
          const summary = details.querySelector(':scope > summary');
          if (typeof drawer?.closeMenuDrawer === 'function') drawer.closeMenuDrawer(new Event('cs-overlay-close'), summary);
          else {
            details.open = false;
            document.body.classList.remove('overflow-hidden-mobile');
          }
        },
      });
      details.addEventListener('toggle', () => {
        if (details.open) {
          if (coordinator.current?.root !== details) coordinator.open('filter', details.querySelector('summary'));
        } else if (coordinator.current?.root === details) {
          coordinator.close('filter');
        }
      });
    });
  };

  const initializeCookiePreferences = () => {
    if (window.__csCookiePreferencesReady) return;
    window.__csCookiePreferencesReady = true;
    const buttons = () => [...document.querySelectorAll('[data-cs-cookie-preferences]')];
    const resolveHandler = () => {
      // Shopify's native Online Store banner exports this documented global.
      if (typeof window.privacyBanner?.showPreferences === 'function') return () => window.privacyBanner.showPreferences();
      if (typeof window.Shopify?.customerPrivacy?.showPreferences === 'function') return () => window.Shopify.customerPrivacy.showPreferences();
      if (typeof window.Shopify?.privacyBanner?.showPreferences === 'function') return () => window.Shopify.privacyBanner.showPreferences();
      const nativeControl = document.querySelector('#shopify-pc__banner__btn-manage-prefs, [data-testid="manage-preferences"]');
      return nativeControl instanceof HTMLElement ? () => nativeControl.click() : null;
    };
    const sync = () => {
      const handler = resolveHandler();
      buttons().forEach((button) => {
        button.hidden = !handler;
        button.onclick = handler ? () => handler() : null;
      });
      return Boolean(handler);
    };
    if (sync()) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (sync() || attempts >= 15) window.clearInterval(timer);
    }, 1000);
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-cs-announcement]').forEach((root) => {
      if (root.dataset.csReady) return;
      root.dataset.csReady = 'true';
      const instance = new AnnouncementRotator(root);
      root.__csController = instance;
      instances.add(instance);
    });

    scope.querySelectorAll('[data-cs-header]').forEach((header) => {
      if (header.dataset.csReady) return;
      header.dataset.csReady = 'true';
      const controller = new HeaderController(header);
      const menuRoot = document.getElementById(header.querySelector('[data-cs-menu-open]')?.getAttribute('aria-controls'));
      const menu = menuRoot ? new MaisonMenu(menuRoot, header.querySelector('[data-cs-menu-open]')) : null;
      const mega = new MegaNavigation(header);
      const searchRoot = document.querySelector('[data-cs-search-overlay]');
      const search = searchRoot ? new SearchOverlay(searchRoot, [...document.querySelectorAll('[data-cs-search-open]')].filter((trigger) => !trigger.closest('[data-cs-menu]'))) : null;
      header.__csControllers = [controller, menu, mega, search].filter(Boolean);
      header.__csControllers.forEach((instance) => instances.add(instance));
    });

    registerExternalOverlays(scope);
    initializeCookiePreferences();
  };

  const destroy = (scope) => {
    scope.querySelectorAll('[data-cs-announcement]').forEach((root) => {
      root.__csController?.destroy?.();
      root.__csController = null;
    });
    scope.querySelectorAll('[data-cs-header]').forEach((header) => {
      header.__csControllers?.forEach((instance) => instance.destroy?.());
      header.__csControllers = [];
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
